import { PropsWithChildren, useEffect, useState } from 'react';
import { fetchTranslates } from 'api/http-client/queries/strapi';
import { isVac } from 'utilities/is-vac';
import { applyVacEnglishCopy, TranslatesByLang, vacSeed } from 'utilities/vac-copy';
import {
  readVacTranslatesCache,
  writeVacTranslatesCache,
} from 'utilities/vac-translates-cache';
import vacStrapiSnapshot from 'utilities/vac-strapi-snapshot.json';
import { Language } from './enums';
import { TranslatesContext } from './context';
import { getLangFromLocalStorage, obtainTransl, setLangToLocalStorage } from './helpers';

interface Props {}

const htmlLangFor = (lang: string) => (lang === Language.FRENCH ? 'fr' : 'en');

const snapshotByLang = (): TranslatesByLang | null => {
  const snapshot = vacStrapiSnapshot as TranslatesByLang;
  if (snapshot?.[Language.ENGLISH] || snapshot?.[Language.FRENCH]) {
    return snapshot;
  }
  return null;
};

const initialTranslates = (): TranslatesByLang | undefined => {
  if (!isVac()) {
    return undefined;
  }

  return applyVacEnglishCopy(readVacTranslatesCache() || snapshotByLang() || vacSeed());
};

export const TranslatesProvider = (props: PropsWithChildren<Props>) => {
  const [selectedLang, setSelectedLang] = useState(getLangFromLocalStorage());
  const [translates, setTranslatesState] = useState<TranslatesByLang | undefined>(initialTranslates);

  const setLang = (lang: string) => {
    setSelectedLang(lang);
    setLangToLocalStorage(lang);
  };

  const setTranslates = (raw: any) => {
    const obtained = obtainTransl(raw);
    if (isVac()) {
      writeVacTranslatesCache(obtained);
    }
    setTranslatesState(applyVacEnglishCopy(obtained));
  };

  useEffect(() => {
    document.documentElement.lang = htmlLangFor(selectedLang);
  }, [selectedLang]);

  useEffect(() => {
    fetchTranslates()
      .then(setTranslates)
      .catch(() => undefined);
  }, []);

  return (
    <TranslatesContext.Provider
      value={{
        selectedLang,
        translates: (translates ? translates[selectedLang] : {}),
        setTranslates,
        setSelectedLang: (lang: string) => setLang(lang),
      }}
    >
      {props.children}
    </TranslatesContext.Provider>
  );
};
